import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { ArrowTopRight } from '@vy/ui-design-system';
import {
  copilotKpiGrid,
  copilotKpiGridCompact,
  copilotKpiCard,
  copilotKpiCardCompact,
  copilotKpiCardLabel,
  copilotKpiCardLabelCompact,
  copilotKpiCardValue,
  copilotKpiCardValueCompact,
  copilotKpiTrendRow,
  copilotSectionTitle,
} from './styles';
import type { KpiItem, VisualizationLink } from './types';
import { CopilotVisualizationLink } from './CopilotVisualizationLink';

type TrendTone = 'positive' | 'negative' | 'neutral';

const TREND_COLORS: Record<TrendTone, string> = {
  positive: '#42ae5c',
  negative: '#ee2049',
  neutral: '#7e8796',
};

// A KPI can carry its trend either as { trend: { value, sentiment } } or as the
// { change, direction } pair the report tools emit. Normalise both into one shape.
function resolveTrend(item: KpiItem): { value: string; tone: TrendTone } | null {
  if (item.trend) return { value: item.trend.value, tone: item.trend.sentiment };
  if (item.change && item.direction) {
    const tone: TrendTone =
      item.direction === 'up' ? 'positive' : item.direction === 'down' ? 'negative' : 'neutral';
    return { value: item.change, tone };
  }
  return null;
}

function KpiCard({ compact, ...item }: KpiItem & { compact?: boolean }) {
  const { label, value } = item;
  const trend = resolveTrend(item);
  const trendColor = trend ? TREND_COLORS[trend.tone] : undefined;
  const rotation =
    trend?.tone === 'negative' ? 'rotate(90deg)' : trend?.tone === 'neutral' ? 'rotate(45deg)' : undefined;

  return (
    <Box sx={compact ? copilotKpiCardCompact : copilotKpiCard}>
      <Typography sx={compact ? copilotKpiCardLabelCompact : copilotKpiCardLabel}>{label}</Typography>
      <Typography sx={compact ? copilotKpiCardValueCompact : copilotKpiCardValue}>{value}</Typography>
      {trend && (
        <Box sx={copilotKpiTrendRow}>
          <ArrowTopRight
            width={12}
            height={12}
            viewBox="0 0 24 24"
            color={trendColor}
            style={{ transform: rotation, flexShrink: 0, display: 'block' }}
          />
          <Typography
            sx={{
              fontFamily: 'Inter, sans-serif',
              fontWeight: 400,
              fontSize: 12,
              lineHeight: '16px',
              color: trendColor,
            }}
          >
            {trend.value}
          </Typography>
        </Box>
      )}
    </Box>
  );
}

type CopilotKpiRowProps = {
  title?: string;
  items: KpiItem[];
  link?: VisualizationLink;
  compact?: boolean;
};

export function CopilotKpiRow({ title, items, link, compact }: CopilotKpiRowProps) {
  return (
    <Box>
      <Typography sx={copilotSectionTitle}>{title ?? 'Key metrics'}</Typography>
      <Box sx={compact ? copilotKpiGridCompact : copilotKpiGrid}>
        {items.map((item) => (
          <KpiCard key={item.label} compact={compact} {...item} />
        ))}
      </Box>
      {link && <CopilotVisualizationLink {...link} />}
    </Box>
  );
}
